import { useDiorama } from "../../contexts/DioramaContext";
import logo from "../../assets/logo.webp";
import StepNavigation from "./StepNavigation";
import BackgroundSelector from "./steps/BackgroundSelector";
import CharacterSelector from "./steps/CharacterSelector";
import TextCustomizer from "./steps/TextCustomizer";
import FinalReview from "./steps/FinalReview";
import DioramaPreview from "../DioramaPreview";
import { Suspense, useRef } from "react";
import ErrorBoundary from "../ErrorBoundary";

const DioramaWizard: React.FC = () => {
	const { state } = useDiorama();
	const helpRef = useRef<HTMLDivElement>(null);

	const renderCurrentStep = () => {
		switch (state.currentStep) {
			case 1:
				return <BackgroundSelector />;
			case 2:
				return <CharacterSelector />;
			case 3:
				return <TextCustomizer />;
			case 4:
				return <FinalReview />;
			default:
				return <BackgroundSelector />;
		}
	};

	return (
		<div className="container mx-auto p-4">
			<header className="flex flex-col lg:flex-row lg:justify-between mb-4">
				<a href="/" title="Gamer Finds" className="mb-6">
					<h1 className="text-3xl font-bold text-center mb-6 text-white flex items-center lg:items-start justify-center  lg:justify-stretch">
						<img src={logo} alt="Gamer Finds" width={100} className="mr-3" />
						<span className="text-left self-center">
							Pixellize{" "}
							<sup>
								<small>Beta</small>
							</sup>
						</span>
					</h1>
				</a>
				<h3 className="text-xl font-semibold mb-4 text-white self-center">
					<button
						onClick={() => {
							if (helpRef.current) {
								helpRef.current.scrollIntoView({
									behavior: "smooth",
									block: "start",
								});
							}
						}}
						className="rounded-xl bg-white text-black px-4 py-2 cursor-pointer"
					>
						Help
					</button>
				</h3>
			</header>

			<div className="flex flex-col lg:flex-row gap-6">
				{/* Preview area */}
				<div className="lg:w-1/2">
					<div className="rounded-md shadow-xl">
						<h2 className="text-xl font-semibold border-t border-t-gray-600 text-white bg-black px-4 pt-1 pb-2 rounded-tl-lg rounded-tr-lg">
							Preview
						</h2>
						<DioramaPreview />
					</div>
				</div>

				{/* Step content */}
				<div className="lg:w-1/2">
					<div className="bg-white rounded-md border border-gray-300 shadow-xl">
						<div className="flex p-4 border-b border-gray-300">
							{[1, 2, 3, 4].map((step) => (
								<div key={step} className="flex-1">
									<div
										className={`h-2 ${
											state.currentStep >= step ? "bg-amber-400" : "bg-gray-200"
										}`}
									/>
									<div className="text-center text-sm mt-1">
										{step === 1 && "Background"}
										{step === 2 && "Characters"}
										{step === 3 && "Text"}
										{step === 4 && "Finish"}
									</div>
								</div>
							))}
						</div>
						<div className="p-4">
							<ErrorBoundary fallback={<h1>Error occurred!</h1>}>
								<Suspense fallback={<h1>Loading...</h1>}>
									{renderCurrentStep()}
								</Suspense>
							</ErrorBoundary>
						</div>
						<div className="p-4 border-t mt-4 border-gray-300">
							<StepNavigation />
							{[2, 3].includes(state.currentStep) && (
								<div className="text-left py-2 mx-auto mt-4">
									Not happy with your choices?{" "}
									<a href="/" className="underline text-amber-600">
										Start over
									</a>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<hr />
			<div id="howto" ref={helpRef} className="mt-6">
				<div className="p-4 rounded-md shadow-md text-white hover:text-gray-300">
					<h2 className="text-2xl text-center font-semibold mb-4">
						How to Use Pixellize
					</h2>
					<p className="mb-4">
						Follow the steps below to create your own diorama.
					</p>
					<video
						width="1024"
						height="1280"
						controls
						className="bg-black w-full h-100 cursor-pointer"
					>
						<source
							src="https://zkikso99xonucyil.public.blob.vercel-storage.com/MEDIA/short%20tutorial%20PIXELLIZE-XJT1qlrRmBYcFjfaLwYXLGRMB0ciGX.mp4"
							type="video/mp4"
						/>
						Your browser does not support the video tag.
					</video>
				</div>
			</div>
			<footer className="mt-6 text-center text-gray-300 text-sm">
				<p>
					&copy; {new Date().getFullYear()} Gamer Finds. All rights reserved.
				</p>
			</footer>
		</div>
	);
};

export default DioramaWizard;
